/**
 * Utility for parsing shell-like command strings into arrays.
 * Handles quoted strings (double quotes) properly.
 */

/**
 * Parse a shell-like command string into an array of arguments.
 * Respects double-quoted strings containing spaces.
 *
 * @example
 * parseCommand('alembic upgrade head')
 * // Returns: ['alembic', 'upgrade', 'head']
 *
 * @example
 * parseCommand('alembic revision -m "init commit"')
 * // Returns: ['alembic', 'revision', '-m', 'init commit']
 *
 * @example
 * parseCommand('python manage.py migrate --database="default"')
 * // Returns: ['python', 'manage.py', 'migrate', '--database=default']
 */
export function parseCommand(input: string): string[] {
  if (!input || !input.trim()) {
    return [];
  }

  const args: string[] = [];
  // Regex: match either quoted strings (capturing content without quotes) or non-space sequences
  // [^\s"]+  : matches any sequence of non-space, non-quote characters
  // "([^"]*)" : matches quoted strings, capturing the content inside quotes
  const regex = /[^\s"]+|"([^"]*)"/gi;
  let match;

  while ((match = regex.exec(input)) !== null) {
    // If match[1] exists, it's the content inside quotes (without the quotes)
    // Otherwise, use match[0] which is the full match (unquoted argument)
    args.push(match[1] !== undefined ? match[1] : match[0]);
  }

  return args;
}

/**
 * Convert an array of command arguments back to a string.
 * Arguments containing spaces will be quoted.
 *
 * @example
 * commandToString(['alembic', 'revision', '-m', 'init commit'])
 * // Returns: 'alembic revision -m "init commit"'
 */
export function commandToString(args: string[] | null | undefined): string {
  if (!args || !Array.isArray(args)) {
    return '';
  }

  return args
    .map(arg => (arg.includes(' ') ? `"${arg}"` : arg))
    .join(' ');
}
